import React from 'react';
import { Button, RestrictedButton } from '@fullstory/components';
import { BulkAction } from './types';

interface BulkActionComponentProps {
  label: string;
  icon: React.ComponentProps<typeof Button>['iconLeft'];
  onClick: () => void;
  restricted: boolean;
  restrictedTooltip?: string;
}

function BulkActionComponent({
  label,
  icon,
  restricted,
  restrictedTooltip,
  ...props
}: BulkActionComponentProps) {
  return (
    <RestrictedButton
      data-component="BulkActionComponent"
      variant="minimal"
      disabled={restricted}
      iconLeft={icon}
      tooltip={restrictedTooltip}
      {...props}
    >
      {label}
    </RestrictedButton>
  );
}

interface UseBulkActionConfig<R> extends Omit<BulkActionComponentProps, 'onClick' | 'restricted'> {
  key: string;
  action: (rows: Set<R>, allRowsSelected: boolean) => void;
  restricted?: boolean | ((rows: Set<R>) => boolean);
}

/**
 * Helper to create a bulk action.
 * Consumers of BulkActionTable should use this to configure
 * bulk actions and pass them into the bulkActions prop.
 *
 * @param config Configuration for the button, including the label, icon, and callback to perform on the set of selected IDs.
 useBulk * @returns A BulkAction to render in the table.
 */
export function makeBulkAction<R>({
  key,
  action,
  restricted = false,
  ...props
}: UseBulkActionConfig<R>): BulkAction<R> {
  return {
    key,
    action,
    // eslint-disable-next-line react/display-name
    render: (onClick, rows) => {
      const isActionRestricted = typeof restricted === 'boolean' ? restricted : restricted(rows);
      return <BulkActionComponent onClick={onClick} restricted={isActionRestricted} {...props} />;
    },
  };
}
